import { useEffect, useMemo, useState } from "react";

import { DashboardCalendarEventResponse, DashboardCalendarParams, DashboardCalendarResponse } from "types";
import { DateUtils } from "utils";

type CalendarEventLoader = (params: DashboardCalendarParams) => Promise<DashboardCalendarResponse>;

const getVisibleRange = (date: Date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  return {
    from: DateUtils.formatDateLocal(start),
    to: DateUtils.formatDateLocal(end),
  };
};

const useCalendarEvents = (loadCalendar: CalendarEventLoader, refreshKey = 0, enabled = true) => {
  const [displayedDate, setDisplayedDate] = useState(new Date());
  const [events, setEvents] = useState<DashboardCalendarEventResponse[]>([]);
  const visibleRange = useMemo(() => getVisibleRange(displayedDate), [displayedDate]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let mounted = true;

    const loadEvents = async () => {
      try {
        const data = await loadCalendar(visibleRange);

        if (mounted) {
          setEvents(Array.isArray(data?.events) ? data.events : []);
        }
      } catch {
        if (mounted) {
          setEvents([]);
        }
      }
    };

    void loadEvents();

    return () => {
      mounted = false;
    };
  }, [enabled, loadCalendar, refreshKey, visibleRange]);

  return { displayedDate, events, setDisplayedDate };
};

export default useCalendarEvents;
